import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { DatePicker } from "antd";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { getEmployeeAttendanceByDate, getPunchByMonth } from "../../../core/data/redux/attendance/punch/punchAction";
import dayjs, { Dayjs } from "dayjs";
import { fetchAllDepartments } from "../../../core/data/redux/department/departmentAction";
import { fetchAllProjects } from "../../../core/data/redux/projects/project/projectAction";
import { getEmployeeAttendancehierarchy } from "../../../core/data/redux/attendance/punch/punchAction";
import { RightOutlined } from "@ant-design/icons";
import EmployeeCalendar from "./attendanceCalendar";

function findNodeById(list: any[], id: number): any | null {
    for (const node of list || []) {
        if (node.EmployeeID === id) return node;
        const hit = findNodeById(node.Subordinates || [], id);
        if (hit) return hit;
    }
    return null;
}

type Employee = {
    EmployeeID: number;
    EmployeeName: string;
    PictureURL?: string | null;
    Phone?: string | null;
    Department?: string | null;
    Designation?: string | null;
    PunchIn?: string | null;
    PunchOut?: string | null;
    Duration?: string | null;
    Subordinates: Employee[];
};

export const EmployeeNode: React.FC<{ employee: Employee; onClick?: (e: Employee) => void; showChevron?: boolean; onOpenCalendar?: (e: Employee) => void; }> = ({ employee, onClick, showChevron = true, onOpenCalendar }) => {
    return (
        <div
            onClick={() => onClick?.(employee)}
            style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                columnGap: 10,
                width: 300,
                padding: "12px 16px",
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                cursor: onClick ? "pointer" : "default",
                position: "relative",
            }}
        >

            <img
                src={employee.PictureURL || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                alt="profile"
                onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "50%", background: "#f3f4f6", border: "2px solid #e5e7eb", }}
            />

            <div style={{ minWidth: 0 }}>
                <strong
                    className="text-truncate"
                    style={{ display: "block", fontSize: 15, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    title={employee.EmployeeName}
                >
                    {employee.EmployeeName}
                </strong>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}> {(employee.Designation || "No designation")} {employee.Department ? ` • ${employee.Department}` : ""} </div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>📞 {employee.Phone || "--"} </div>
            </div>

            <div style={{ display: showChevron && onClick ? "block" : "none" }}><RightOutlined style={{ fontSize: 14, color: "#6b7280" }} /></div>

            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", gap: 12, marginTop: 8, fontSize: 12, color: "#374151" }}>
                <div><strong style={{ color: "#16a34a" }}>In:</strong>{employee.PunchIn ? new Date(employee.PunchIn).toLocaleTimeString() : "--"} </div>
                <div><strong style={{ color: "#dc2626" }}>Out:</strong>{employee.PunchOut ? new Date(employee.PunchOut).toLocaleTimeString() : "--"}</div>
                <div>⏱ <span style={{ fontWeight: 600 }}>{employee.Duration || "--"}</span></div>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenCalendar?.(employee);
                }}
                style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 12,
                    cursor: "pointer",
                    zIndex: 10,
                }}
            >
                📅
            </button>
        </div>
    );
};
const AttendanceManager = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [employeeAttendancehir, setemployeeAttendancehir] = useState<any[]>([])
    const orgViewportRef = useRef<HTMLDivElement | null>(null);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [selectedPunchMonth, setSelectedPunchMonth] = useState([]);
    const employeeId = employeesbyUser?.employeeId;

    const openCalendarForEmployee = async (emp: Employee) => {
        setSelectedEmployee(emp);

        const month = dayjs().format("YYYY-MM"); // default current month

        const res: any = await dispatch(
            getPunchByMonth({
                employeeId: emp.EmployeeID,
                month,
            })
        );

        setSelectedPunchMonth(res.payload.data || []);
        setCalendarModalOpen(true);
    };

    const filteredHierarchy = useMemo(() => {
        if (!Array.isArray(employeeAttendancehir) || employeeAttendancehir.length === 0) return [];
        if (!employeeId) return employeeAttendancehir;

        const me = findNodeById(employeeAttendancehir, employeeId);
        return me ? [me] : employeeAttendancehir;
    }, [employeeAttendancehir, employeeId]);

    function firstRoot(data: Employee[]): Employee | null {
        if (!Array.isArray(data) || data.length === 0) return null;
        return data[0];
    }

    const EmployeeAttendanceTree: React.FC<{ data: Employee[] }> = ({ data }) => {
        const root = useMemo(() => firstRoot(data), [data]);
        const [path, setPath] = useState<Employee[]>(() => (root ? [root] : []));
        const current = path[path.length - 1];

        const drillDown = (emp: Employee) => setPath((prev) => [...prev, emp]);
        const nextLevel = (index: number) => setPath((prev) => prev.slice(0, index + 1));
        const goTop = () => root && setPath([root]);

        if (!root) return <div>No employee data available</div>;

        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "8px 0 10px" }}>
                {path.map((emp, idx) => (
                    <React.Fragment key={emp.EmployeeID}>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <EmployeeNode
                                employee={emp}
                                onClick={() => (idx === path.length - 1 ? undefined : nextLevel(idx))}
                                showChevron={idx !== path.length - 1}
                                onOpenCalendar={openCalendarForEmployee}
                            />
                        </div>
                        {idx < path.length - 1 && (
                            <div
                                style={{
                                    width: 2,
                                    height: 20,
                                    background: "#e5e7eb",
                                    borderRadius: 2,
                                }}
                            />
                        )}
                    </React.Fragment>
                ))}

                <div
                    style={{
                        width: "100%",
                        maxWidth: 1080,
                        border: "2px solid #5b5bf7",
                        background: "#ffffff",
                        borderRadius: 12,
                        padding: 16,
                        marginTop: 8,
                    }}
                >
                    {current.Subordinates?.length ? (
                        <div
                            style={{
                                display: "grid",
                                gridAutoFlow: "column",
                                gridAutoColumns: "min-content",
                                gap: 14,
                                overflowX: "auto",
                                padding: 2,
                            }}
                        >
                            {current.Subordinates.map((s) => (
                                <div key={s.EmployeeID} style={{ display: "inline-flex" }}>
                                    <EmployeeNode employee={s} onClick={drillDown} onOpenCalendar={openCalendarForEmployee} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            style={{
                                color: "#6b7280",
                                fontSize: 13,
                                padding: 12,
                                border: "1px dashed #e5e7eb",
                                borderRadius: 8,
                                background: "#fafafa",
                                textAlign: "center",
                                cursor: "pointer"
                            }}
                            onClick={goTop}
                        >
                            No direct subordinates. (Click to go back to top)
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleDateChange = (value: any) => {
        if (value) {
            const formattedDate = dayjs(value).format("YYYY-MM-DD");
            setSelectedDate(formattedDate);
        }
    };
    const disableFutureDates = (current: dayjs.Dayjs) => {
        return current && current > dayjs().endOf("day");
    };

    useEffect(() => {
        dispatch(getEmployeeAttendanceByDate(selectedDate));
        dispatch(fetchAllProjects());
        dispatch(fetchAllDepartments());
        dispatch(getEmployeeAttendancehierarchy(selectedDate))
            .then((res) => {
                setemployeeAttendancehir(res.payload.data)
            })
    }, [dispatch, selectedDate]);

    const getModalContainer = () =>
        document.getElementById("modal-datepicker") || document.body;

    const handleMonthChange = async (monthValue: Dayjs) => {
        if (!selectedEmployee) return;

        const formattedMonth = monthValue.format("YYYY-MM");

        const res: any = await dispatch(
            getPunchByMonth({
                employeeId: selectedEmployee.EmployeeID,
                month: formattedMonth,
            })
        );

        setSelectedPunchMonth(res.payload.data || []);
    };


    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Attendance Manager</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Attendance</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Attendance Manager
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div
                                className="position-relative d-inline-block "
                                style={{ width: 160 }}
                            >
                                <i
                                    className="ti ti-file-analytics text-white position-absolute"
                                    style={{
                                        left: 5,
                                        top: "40%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                    }}
                                />
                                <DatePicker
                                    className="form-control border-0 ps-4 bg-primary text-white shadow-none"
                                    format={{
                                        format: "DD-MM-YYYY",
                                        type: "mask",
                                    }}
                                    getPopupContainer={getModalContainer}
                                    placeholder="DD-MM-YYYY"
                                    onChange={handleDateChange}
                                    disabledDate={disableFutureDates}
                                    value={dayjs(selectedDate)}
                                    style={{ width: "100%" }}
                                />
                            </div>

                            <div className="ms-2 head-icons">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>📊 Employee Today's Attendance</h5>
                        </div>
                        <div className="card-body p-0">
                            <div ref={orgViewportRef} className="org-viewport container mt-4">
                                <div className="org-canvas">
                                    <EmployeeAttendanceTree data={filteredHierarchy} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {calendarModalOpen && selectedEmployee && (
                <div
                    className="modal fade show"
                    style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5>
                                    {selectedEmployee.EmployeeName}
                                </h5>
                                <button className="btn-close" onClick={() => setCalendarModalOpen(false)} />
                            </div>

                            <div className="modal-body">
                                <EmployeeCalendar
                                    punchData={selectedPunchMonth}
                                    employeeId={selectedEmployee.EmployeeID}
                                    onMonthChange={handleMonthChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default AttendanceManager;
